import React, { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * Pure three.js (no R3F JSX) — sidesteps the visual-edits Babel plugin
 * that injects unknown props into every JSX node and breaks three.js elements.
 */
export default function Hero3D() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 1.4, 4.8);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Lighting
    scene.add(new THREE.AmbientLight(0xd4af37, 0.4));
    const dir = new THREE.DirectionalLight(0xf2a65a, 1.8);
    dir.position.set(3, 5, 2);
    scene.add(dir);
    const spot = new THREE.SpotLight(0xe69138, 0.8, 30, 0.6);
    spot.position.set(-3, 3, -2);
    scene.add(spot);
    const point = new THREE.PointLight(0x4a0e16, 0.4);
    point.position.set(0, -2, 0);
    scene.add(point);

    // Group containing plate + curry
    const group = new THREE.Group();

    // Bottom plate
    const bottom = new THREE.Mesh(
      new THREE.CylinderGeometry(1.6, 1.7, 0.08, 64),
      new THREE.MeshStandardMaterial({ color: 0x1a0b0e, metalness: 0.4, roughness: 0.3 })
    );
    bottom.position.y = -0.15;
    group.add(bottom);

    // Gold rim
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(1.55, 0.04, 16, 80),
      new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 1, roughness: 0.15, emissive: 0x8a6f1f, emissiveIntensity: 0.25 })
    );
    rim.position.y = -0.1;
    rim.rotation.x = Math.PI / 2;
    group.add(rim);

    // Inner plate
    const inner = new THREE.Mesh(
      new THREE.CylinderGeometry(1.3, 1.3, 0.04, 64),
      new THREE.MeshStandardMaterial({ color: 0x2a1116, metalness: 0.6, roughness: 0.4 })
    );
    inner.position.y = -0.08;
    group.add(inner);

    // Curry mound
    const curry = new THREE.Mesh(
      new THREE.SphereGeometry(0.62, 32, 32),
      new THREE.MeshStandardMaterial({ color: 0xe69138, roughness: 0.7, metalness: 0.1, emissive: 0xcf6f1a, emissiveIntensity: 0.18 })
    );
    curry.position.y = 0.05;
    curry.scale.y = 0.7;
    group.add(curry);

    // Saffron swirl
    const swirl = new THREE.Mesh(
      new THREE.TorusGeometry(0.18, 0.05, 16, 32),
      new THREE.MeshStandardMaterial({ color: 0xf2a65a, emissive: 0xf2a65a, emissiveIntensity: 0.45 })
    );
    swirl.position.set(0, 0.42, 0);
    swirl.rotation.x = Math.PI / 2;
    group.add(swirl);

    // Garnish (8 green peas around top)
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const pea = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 16, 16),
        new THREE.MeshStandardMaterial({ color: 0x5fa84a, roughness: 0.4 })
      );
      pea.position.set(Math.cos(angle) * 0.45, 0.32, Math.sin(angle) * 0.45);
      group.add(pea);
    }

    // Naan piece (torus segment)
    const naan = new THREE.Mesh(
      new THREE.TorusGeometry(0.28, 0.08, 16, 32, Math.PI),
      new THREE.MeshStandardMaterial({ color: 0xd9b074, roughness: 0.85 })
    );
    naan.position.set(1.05, -0.02, 0.55);
    naan.rotation.set(Math.PI / 2.4, 0, 0.4);
    group.add(naan);

    // Sparkle particles
    const sparkleCount = 80;
    const sparkleGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(sparkleCount * 3);
    for (let i = 0; i < sparkleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 5;
      positions[i * 3 + 1] = (Math.random() - 0.3) * 3;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 4;
    }
    sparkleGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const sparkleMat = new THREE.PointsMaterial({ color: 0xd4af37, size: 0.04, transparent: true, opacity: 0.85 });
    const sparkles = new THREE.Points(sparkleGeo, sparkleMat);
    scene.add(sparkles);

    scene.add(group);

    // Mouse interaction
    let targetRotX = 0;
    let targetRotY = 0;
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      targetRotY = x * 0.6;
      targetRotX = y * 0.3;
    };
    container.addEventListener("mousemove", onMove);

    // Animate
    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      const t = clock.getElapsedTime();
      group.rotation.y += (targetRotY + t * 0.15 - group.rotation.y) * 0.05;
      group.rotation.x += (targetRotX - group.rotation.x) * 0.05;
      group.position.y = Math.sin(t * 1.4) * 0.06;
      sparkles.rotation.y = t * 0.05;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    // Resize
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      container.removeEventListener("mousemove", onMove);
      renderer.dispose();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
          else o.material.dispose();
        }
      });
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" data-testid="hero-3d-canvas" />;
}
